import React, { useState } from 'react';
import { VisibilityOutlined as PreviewIcon } from '@mui/icons-material';

/**
 * Preview-before-Download for any generated PDF (Invoice, Gate Pass, Purchase, custom Reports).
 * Added 2026-09-14 per explicit long-standing request — every "PDF" button used to force an
 * immediate save-as with no way to check the document first.
 *
 * `fetchBlob` is a caller-supplied async function returning the PDF Blob (so each screen keeps
 * its own endpoint/auth/body — this component doesn't assume GET-with-no-body). Preview opens
 * an inline viewer (browsers render PDFs natively in an iframe); Download saves it to disk.
 * Both share the same fetched blob within one open/close cycle so a Preview-then-Download
 * doesn't re-fetch.
 */
const PdfActions = ({ fetchBlob, filename = 'document.pdf', label = 'PDF', className = 'btn-primary' }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobCache, setBlobCache] = useState(null);

  const getBlob = async () => {
    if (blobCache) return blobCache;
    const blob = await fetchBlob();
    setBlobCache(blob);
    return blob;
  };

  const handlePreview = async () => {
    setLoading(true); setError(null);
    try {
      const blob = await getBlob();
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (e) {
      setError(e.message || `Failed to load ${label}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true); setError(null);
    try {
      const blob = await getBlob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objUrl);
    } catch (e) {
      setError(e.message || `Failed to download ${label}`);
    } finally {
      setLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setBlobCache(null);
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {/* Preview matches the size of the download button (compact inside table rows) */}
      <button className={className.includes('action-btn') ? 'action-btn btn-secondary' : 'btn-secondary'}
        onClick={handlePreview} disabled={loading} title={`Preview ${label}`}>
        <PreviewIcon sx={{ fontSize: 15 }} aria-hidden="true" /> Preview
      </button>
      <button className={className} onClick={handleDownload} disabled={loading} title={`Download ${label}`}>{label}</button>
      {error && <span style={{ color: 'var(--color-error)', fontSize: 11 }}>{error}</span>}
      {previewUrl && (
        <div className="modal-overlay" onClick={closePreview} style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: 960, width: '92%', height: '88vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>{label} Preview</h3>
              <button className="modal-close" onClick={closePreview}>&times;</button>
            </div>
            <iframe title={`${label}-preview`} src={previewUrl} style={{ flex: 1, border: 'none', width: '100%' }} />
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid var(--color-border-medium)' }}>
              <button className="btn-secondary" onClick={closePreview}>Close</button>
              <button className="btn-primary" onClick={handleDownload}>Download</button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default PdfActions;
